import { useCallback, useEffect, useMemo, useState } from 'react'
import { getApiErrorMessage } from '../../../shared/api/apiClient'
import { SECTION_ACTION } from '../config/draftReviewConfig'
import {
  applyPendingEdits,
  findSection,
  getSectionValue,
  replaceSection,
  toPendingEdit,
} from '../helpers/draftReviewHelpers'
import { mapSectionWriteFailure } from '../mappers/draftReviewMapper'
import { brandExtractionService as sharedExtractionService } from '../services/brandExtractionService'

const EMPTY_REVIEW = Object.freeze({
  approvedCount: 0,
  canSendToPublish: false,
  documentCount: 0,
  documents: [],
  emptyCount: 0,
  groups: [],
  jobId: '',
  pendingConflictCount: 0,
  rejectedCount: 0,
  requestId: '',
  sectionCount: 0,
  totalSectionCount: 0,
})

// Someone else changed a row since this screen loaded it. The whole write is
// rejected, so the only safe recovery is to re-read and let them look again.
const STALE_ROW_STATUS = 409

// Owns the extracted sections and every decision taken against them. Writes
// go through one endpoint that takes a list, so a single card's decision and
// a whole group's are the same call with a different number of items.
export const useDraftReview = ({
  brand,
  extractionService,
  isEnabled = true,
}) => {
  const service = extractionService || sharedExtractionService
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  // Conflict picks the reviewer has made but not yet committed, keyed by
  // section id. Nothing here has reached the API.
  const [pendingEdits, setPendingEdits] = useState({})
  const [review, setReview] = useState(EMPTY_REVIEW)

  const loadReview = useCallback(async () => {
    if (!brand?.id) return
    setError('')
    setIsLoading(true)
    try {
      // A fresh read is the server's word on every section, so anything the
      // reviewer had not committed is gone with it.
      setPendingEdits({})
      setReview(await service.getDraftReview(brand))
    } catch (requestError) {
      setReview(EMPTY_REVIEW)
      setError(
        getApiErrorMessage(
          requestError,
          'Extracted sections could not be loaded.',
        ),
      )
    } finally {
      setIsLoading(false)
    }
  }, [brand, service])

  useEffect(() => {
    if (!isEnabled) return
    loadReview()
  }, [isEnabled, loadReview])

  // Sends a list of decisions as one transaction and folds the sections that
  // come back into place. `row_version` is filled in per item from what this
  // screen last saw, which is what lets the backend reject a stale write.
  // Resolves to whether the write went through.
  const applyDecisions = useCallback(
    async (decisions = []) => {
      if (decisions.length === 0) return false
      setError('')
      setIsSaving(true)
      try {
        const { updated, ...summary } = await service.saveSectionDecisions(
          brand,
          decisions.map((decision) => {
            const section = findSection(review.groups, decision.sectionId)
            const pendingEdit = pendingEdits[decision.sectionId]
            const isReset = decision.action === SECTION_ACTION.RESET
            return {
              rowVersion: section?.rowVersion,
              ...decision,
              candidateId: isReset
                ? ''
                : decision.candidateId ||
                  pendingEdit?.selectedCandidateId ||
                  section?.selectedCandidateId ||
                  '',
              value: isReset
                ? ''
                : (decision.value ??
                  pendingEdit?.value ??
                  getSectionValue(section) ??
                  ''),
            }
          }),
        )
        setReview((current) =>
          updated.reduce(
            (next, section) => ({
              ...next,
              groups: replaceSection(next.groups, section.id, section),
            }),
            // The write response carries refreshed counters, so the header
            // never has to be recomputed or re-fetched.
            { ...current, ...summary },
          ),
        )
        // The decision is recorded, so the held edit has served its purpose
        // and the section now renders from what came back.
        setPendingEdits((current) =>
          decisions.reduce((next, { sectionId }) => {
            const { [sectionId]: committed, ...rest } = next
            return committed ? rest : next
          }, current),
        )
        return true
      } catch (requestError) {
        const { message } = mapSectionWriteFailure(requestError?.payload)
        setError(
          message ||
            getApiErrorMessage(requestError, 'That change could not be saved.'),
        )
        if (requestError?.status === STALE_ROW_STATUS) await loadReview()
        return false
      } finally {
        setIsSaving(false)
      }
    },
    [brand, loadReview, pendingEdits, review.groups, service],
  )

  // Approves a group's sections in one write. Each goes out with whatever the
  // reviewer saved or picked for it, else its extracted value.
  const approveGroup = useCallback(
    (sections) =>
      applyDecisions(
        sections.map(({ id }) => ({
          action: SECTION_ACTION.APPROVE,
          sectionId: id,
        })),
      ),
    [applyDecisions],
  )

  // Save changes on a group: the edited text, keyed by section id, is held on
  // screen until the group is approved. A conflict pick made earlier is kept.
  const editGroup = useCallback(
    (values) =>
      setPendingEdits((current) => ({
        ...current,
        ...Object.fromEntries(
          Object.entries(values).map(([id, value]) => [
            id,
            toPendingEdit({
              candidateId: current[id]?.selectedCandidateId,
              value: value.trim(),
            }),
          ]),
        ),
      })),
    [],
  )

  // Editing sections that are already approved has no Approve step after it,
  // so the new text goes out now as EDIT, with the rest of the payload as is.
  const editApprovedGroup = useCallback(
    (values) =>
      applyDecisions(
        Object.entries(values).map(([sectionId, value]) => ({
          action: SECTION_ACTION.EDIT,
          sectionId,
          value: value.trim(),
        })),
      ),
    [applyDecisions],
  )

  // Undo puts the group's decided sections back to undecided, keeping nothing
  // the reviewer had chosen — the same as never having acted.
  const undoGroup = useCallback(
    (sections) =>
      applyDecisions(
        sections.map(({ id }) => ({
          action: SECTION_ACTION.RESET,
          sectionId: id,
        })),
      ),
    [applyDecisions],
  )

  // Picking one side of a conflict. It does not reach the API on its own: the
  // section is still undecided, and it is the group approval that commits it.
  const resolveSection = useCallback(
    (sectionId, { candidateId }) => {
      const candidate = findSection(review.groups, sectionId)?.candidates?.find(
        ({ id }) => id === candidateId,
      )
      setPendingEdits((current) => ({
        ...current,
        [sectionId]: toPendingEdit({ candidateId, value: candidate?.value }),
      }))
    },
    [review.groups],
  )

  const sendToPublishQueue = useCallback(async () => {
    setError('')
    setIsSending(true)
    try {
      const publication = await service.sendToPublishQueue(brand)
      await loadReview()
      return publication || {}
    } catch (requestError) {
      setError(
        getApiErrorMessage(
          requestError,
          'Sections could not be sent to the publish queue.',
        ),
      )
      return null
    } finally {
      setIsSending(false)
    }
  }, [brand, loadReview, service])

  // Uncommitted edits read exactly like extracted content on the cards, so
  // everything downstream — the counters, whether publish is unblocked — sees
  // the screen as the reviewer has left it.
  const editedReview = useMemo(
    () => applyPendingEdits(review, pendingEdits),
    [pendingEdits, review],
  )

  return {
    approveGroup,
    editApprovedGroup,
    editGroup,
    error,
    isLoading,
    isSaving,
    isSending,
    reloadReview: loadReview,
    resolveSection,
    review: editedReview,
    sendToPublishQueue,
    undoGroup,
  }
}
